import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, Loader2, Info } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { bcAssessmentQuestions, bcAssessmentStart, bcAssessmentAnswer, bcAssessmentComplete } from "@/lib/api";

const INDUSTRIES = ["Restaurant", "Medical / Dental", "Real estate", "Automotive", "Professional services", "Retail / e-commerce", "Hotel / Travel", "Home services", "School / Nonprofit", "Other"];
const SIZES = [
  { key: "micro", label: "1–5 people" },
  { key: "small", label: "6–25 people" },
  { key: "medium", label: "26–100 people" },
  { key: "growth", label: "100+ people" },
];

export default function BusinessCenterAssessment() {
  const nav = useNavigate();
  const [bank, setBank] = useState(null);
  const [step, setStep] = useState(-1);            // -1: profile, 0..N-1: questions, N: contact/report
  const [aid, setAid] = useState(null);
  const [profile, setProfile] = useState({ business_name: "", industry: "", business_size: "small", country: "", locations: 1 });
  const [answers, setAnswers] = useState({});       // question_idx → choice_idx
  const [saving, setSaving] = useState(false);
  const [contact, setContact] = useState({ name: "", email: "", phone: "", consent_marketing: true, consent_report: true });
  const [completeErr, setCompleteErr] = useState(null);

  useEffect(() => {
    document.title = "AI Readiness Assessment | WebsiteTech.pro";
    bcAssessmentQuestions().then(setBank).catch(() => {});
  }, []);

  const questions = bank?.questions || [];
  const totalSteps = questions.length;
  const stepPct = step < 0 ? 0 : Math.min(100, Math.round((step / (totalSteps + 1)) * 100));

  const startFromProfile = async () => {
    setSaving(true);
    try {
      const s = await bcAssessmentStart({ ...profile, referrer: window.location.pathname });
      setAid(s.assessment_id);
      setStep(0);
    } finally { setSaving(false); }
  };

  const pickChoice = async (choiceIdx) => {
    if (!aid) return;
    const qIdx = step;
    setAnswers((a) => ({ ...a, [qIdx]: choiceIdx }));
    try {
      await bcAssessmentAnswer({ assessment_id: aid, question_idx: qIdx, choice_idx: choiceIdx });
    } catch {}
    if (step + 1 < totalSteps) setStep(step + 1);
    else setStep(totalSteps); // contact form
  };

  const submitComplete = async () => {
    if (!aid) return;
    setCompleteErr(null);
    setSaving(true);
    try {
      const r = await bcAssessmentComplete({
        assessment_id: aid, ...contact,
      });
      nav(`/business-center/assessment/${aid}/report`);
    } catch (e) {
      setCompleteErr(e?.response?.data?.detail || e?.message || "Could not compute your report");
    } finally { setSaving(false); }
  };

  const currentQ = step >= 0 && step < totalSteps ? questions[step] : null;

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Navbar />
      <main className="flex-1 pt-24 pb-16" data-testid="bc-assessment">
        <div className="max-w-3xl mx-auto px-4">
          <div className="mb-4 text-xs uppercase tracking-widest text-indigo-700 font-bold flex items-center gap-2">
            <Link to="/business-center" className="hover:text-indigo-900 inline-flex items-center gap-1"><ArrowLeft size={12} /> AI Business Center</Link>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900">AI Readiness Assessment</h1>
          <p className="mt-2 text-slate-600 max-w-2xl">A 5-minute assessment scored across 20 categories. You'll receive a personalized report with recommended solutions and a 30/60/90-day roadmap.</p>

          {/* Progress bar */}
          <div className="mt-6 h-2 bg-slate-200 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-indigo-500 to-fuchsia-500 transition-all" style={{ width: `${stepPct}%` }} data-testid="bc-assessment-progress" />
          </div>
          <div className="mt-1 text-xs text-slate-500">
            {step < 0 ? "Getting started" : step >= totalSteps ? "Almost done — get your report" : `Question ${step + 1} of ${totalSteps}`}
          </div>

          {/* Step: profile */}
          {step === -1 && (
            <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 shadow-sm" data-testid="bc-step-profile">
              <h2 className="text-xl font-semibold text-slate-900">Tell us about your business</h2>
              <p className="mt-1 text-sm text-slate-600">No signup required. Nothing is written to a lead record until you request your report.</p>
              <div className="mt-5 space-y-3">
                <Input label="Business name (optional)" value={profile.business_name} onChange={(v) => setProfile({ ...profile, business_name: v })} testid="bc-profile-name" />
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Industry</Label>
                    <select data-testid="bc-profile-industry" value={profile.industry} onChange={(e) => setProfile({ ...profile, industry: e.target.value })} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm">
                      <option value="">Choose…</option>
                      {INDUSTRIES.map((i) => <option key={i}>{i}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label>Size</Label>
                    <select data-testid="bc-profile-size" value={profile.business_size} onChange={(e) => setProfile({ ...profile, business_size: e.target.value })} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm">
                      {SIZES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Input label="Country (optional)" value={profile.country} onChange={(v) => setProfile({ ...profile, country: v })} testid="bc-profile-country" />
                  <Input label="Number of locations" type="number" value={profile.locations} onChange={(v) => setProfile({ ...profile, locations: Number(v) || 1 })} testid="bc-profile-locations" />
                </div>
              </div>
              <button
                data-testid="bc-profile-continue"
                onClick={startFromProfile}
                disabled={saving}
                className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white font-semibold py-3 transition"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Start the assessment <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Step: question */}
          {currentQ && (
            <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 shadow-sm" data-testid={`bc-step-q-${step}`}>
              <div className="text-[11px] uppercase tracking-widest font-bold text-slate-500">{catLabel(bank, currentQ.category)}</div>
              <h2 className="mt-1 text-xl md:text-2xl font-semibold text-slate-900">{currentQ.prompt}</h2>
              <div className="mt-6 space-y-2">
                {currentQ.choices.map((c, i) => {
                  const picked = answers[step] === i;
                  return (
                    <button
                      key={i}
                      data-testid={`bc-choice-${step}-${i}`}
                      onClick={() => pickChoice(i)}
                      className={`w-full text-left rounded-lg border px-4 py-3 text-sm transition ${picked ? "border-indigo-500 bg-indigo-50 text-indigo-900" : "border-slate-200 hover:border-slate-400 hover:bg-slate-50 text-slate-800"}`}
                    >
                      <div className="font-semibold">{c.label}</div>
                    </button>
                  );
                })}
              </div>
              <div className="mt-6 flex items-center justify-between">
                <button
                  data-testid="bc-back"
                  onClick={() => setStep(Math.max(-1, step - 1))}
                  className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900"
                >
                  <ArrowLeft size={14} /> Back
                </button>
                <div className="text-xs text-slate-500">Question {step + 1} of {totalSteps}</div>
              </div>
            </div>
          )}

          {/* Step: contact/report */}
          {step === totalSteps && (
            <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 shadow-sm" data-testid="bc-step-contact">
              <CheckCircle2 className="w-10 h-10 text-emerald-500" />
              <h2 className="mt-4 text-2xl font-semibold text-slate-900">All done — where should we send your report?</h2>
              <p className="mt-1 text-sm text-slate-600">Your personalized report is ready. Enter your details and we'll compute your score and roadmap, plus save it to your account so you can revisit and edit assumptions later.</p>
              <div className="mt-5 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <Input label="Your name" value={contact.name} onChange={(v) => setContact({ ...contact, name: v })} testid="bc-contact-name" />
                  <Input label="Work email" value={contact.email} onChange={(v) => setContact({ ...contact, email: v })} testid="bc-contact-email" />
                </div>
                <Input label="Phone (optional)" value={contact.phone} onChange={(v) => setContact({ ...contact, phone: v })} testid="bc-contact-phone" />
                <label className="flex items-start gap-2 text-xs text-slate-600">
                  <input type="checkbox" checked={contact.consent_report} onChange={(e) => setContact({ ...contact, consent_report: e.target.checked })} className="mt-0.5" data-testid="bc-contact-consent-report" />
                  <span>I agree to receive my personalized report by email. My data is stored securely and never sold.</span>
                </label>
                <label className="flex items-start gap-2 text-xs text-slate-600">
                  <input type="checkbox" checked={contact.consent_marketing} onChange={(e) => setContact({ ...contact, consent_marketing: e.target.checked })} className="mt-0.5" />
                  <span>Optional: send me the Weekly AI Business Brief.</span>
                </label>
              </div>
              {completeErr && (
                <div className="mt-4 text-xs text-red-800 bg-red-50 border border-red-200 rounded px-3 py-2">{completeErr}</div>
              )}
              <button
                data-testid="bc-complete"
                onClick={submitComplete}
                disabled={!contact.email || !contact.consent_report || saving}
                className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white font-semibold py-3 transition"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Get my report <ArrowRight className="w-4 h-4" />
              </button>
              <div className="mt-4 text-[11px] text-slate-500 flex items-start gap-1.5">
                <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                Our recommendations are rule-based and independent of any affiliate relationships. Sources and testing dates are shown on every product review.
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}

function catLabel(bank, key) {
  const found = (bank?.categories || []).find((c) => c.key === key);
  return found?.label || key;
}
function Label({ children }) {
  return <div className="text-xs uppercase tracking-widest font-semibold text-slate-500 mb-1">{children}</div>;
}
function Input({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <Label>{label}</Label>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none"
      />
    </div>
  );
}
